import {
  contextConditionHolds,
  resolveContextValue,
  type UrlRewriteAction,
  type UrlRewriteBlobTemplateNode,
  type UrlRewriteConfig,
  type UrlRewriteContextCondition,
  type UrlRewriteMatchCriteria,
  type UrlRewriteRule,
} from "@do/shared-types";

const HTTP_SCHEME_PREFIX = /^https?:\/\//i;
const HTTP_PROTOCOL = /^https?:$/;
/** Path ends with a common raster/vector image extension (before query or hash). */
const IMAGE_PATH_EXTENSION = /\.(avif|bmp|gif|ico|jpe?g|png|svg|webp)(\?|#|$)/i;

/**
 * Media hosts whose delivery URLs are images whatever their path: an imgproxy URL carries its
 * signature and processing options, and need not end with an extension.
 */
const STATIC_ASSET_HOSTS = new Set([
  // imgproxy, the media transformer (ADR-0015): one host per environment, and the local stack
  "imgproxy.dialonce.ai",
  "imgproxy.staging.tardis.dialonce.ai",
  "imgproxy.preproduction.tardis.dialonce.ai",
  "localhost:3100",
  // Legacy Cloudinary account, while stored content still holds its URLs: delete this line
  // once the content migration and the grace period are over
  "res.cloudinary.com",
]);

function isLikelyStaticAssetUrl(url: URL): boolean {
  return IMAGE_PATH_EXTENSION.test(url.pathname) || STATIC_ASSET_HOSTS.has(url.host.toLowerCase());
}

export type RewriteUrlOptions = {
  debug?: boolean;
};

const EMPTY_CONTEXT_URL = "https://invalid.local/";

/**
 * Base64-encode a UTF-8 string. Plain `btoa(json)` only accepts Latin-1 and
 * throws on accents/emoji; encode to UTF-8 bytes first when using `btoa`.
 */
function encodeBase64Utf8(text: string): string {
  if (typeof btoa === "function") {
    const bytes = new TextEncoder().encode(text);
    let binary = "";
    for (const byte of bytes) {
      binary += String.fromCharCode(byte);
    }
    return btoa(binary);
  }
  return Buffer.from(text, "utf8").toString("base64");
}

function parseContextUrl(currentUrl: string | null | undefined): URL | null {
  if (!currentUrl) return null;
  try {
    return new URL(currentUrl);
  } catch {
    return null;
  }
}

/**
 * A template string that is exactly one `{{contextKey}}` placeholder. Spaces
 * inside the braces are ignored, so `{{ contextKey }}` resolves the same key
 * instead of silently dropping the entry.
 */
const CONTEXT_PLACEHOLDER = /^\{\{\s*([^{}]+?)\s*\}\}$/;

/** Marks a template node whose context key is missing, so its parent drops it. */
const OMIT = Symbol("omit");

/**
 * Renders an `insertContextBlob` template against the conversation context.
 * A string that is exactly `{{key}}` is replaced by the raw context value (its
 * type is preserved: number stays number). Any other string is kept as a
 * literal. Entries and array items whose key resolves to nothing are omitted.
 */
function renderBlobTemplate(
  node: UrlRewriteBlobTemplateNode,
  conversationContext: Record<string, unknown> | null | undefined
): unknown {
  if (typeof node === "string") {
    const match = CONTEXT_PLACEHOLDER.exec(node);
    if (!match) return node;
    return resolveContextValue(conversationContext, match[1]) ?? OMIT;
  }
  if (Array.isArray(node)) {
    return node
      .map((child) => renderBlobTemplate(child, conversationContext))
      .filter((child) => child !== OMIT);
  }
  // `Object.fromEntries` defines own properties, so a `__proto__` key stays data.
  return Object.fromEntries(
    Object.entries(node)
      .map(([key, child]) => [key, renderBlobTemplate(child, conversationContext)] as const)
      .filter(([, child]) => child !== OMIT)
  );
}

function matchesContextCondition(
  condition: UrlRewriteContextCondition,
  conversationContext: Record<string, unknown> | null | undefined
): boolean {
  return contextConditionHolds(condition, resolveContextValue(conversationContext, condition.key));
}

export type RewriteConversationContextInput = {
  intent?: string | null;
  entities?: Record<string, unknown> | null;
  mergedCtxData?: Record<string, unknown> | null;
  sessionId?: string | null;
};

/**
 * Builds the flat context object consumed by rewrite match conditions and
 * actions. `intent` and `entities` come from the `retrieveParams` endpoint,
 * which already merged the dynamic and the static conversation contexts.
 */
export function buildRewriteConversationContext({
  intent,
  entities,
  mergedCtxData,
  sessionId,
}: RewriteConversationContextInput): Record<string, unknown> {
  return {
    ...(intent ? { intent } : {}),
    ...(entities ? { entities } : {}),
    ...(mergedCtxData ?? {}),
    ...(sessionId ? { sessionId } : {}),
  };
}

/**
 * Transforms a target URL (e.g. a chatbot button URL) by applying rewrite rules
 * scoped to the host page URL and optional conversation-context values. Designed
 * to never throw and to never break a click: any failure or unsupported input
 * returns the original `targetUrl` unchanged.
 *
 * `currentUrl` (host page) is optional. Target-only actions (`appendParam`,
 * `overwriteParam`, `removeParam`) still apply without it. Host-URL-dependent
 * actions (`transferParams`, `rewriteKey`) are no-ops when the host URL is
 * missing or invalid.
 *
 * `conversationContext` is optional. Context-dependent match conditions and
 * actions (`insertContextValue`, `insertContextBlob`, `appendPathSegment`) skip
 * silently when a referenced key is missing (unless `fallback` is set on
 * insertContextValue).
 *
 * Action order within a single rule matters. Actions execute in array order,
 * so `removeParam` placed before `transferParams` removes the param before
 * transfer; placed after, it strips a freshly transferred param.
 *
 * Rules run in array order (first to last).
 */
export function rewriteUrl(
  targetUrl: string,
  config: UrlRewriteConfig | null | undefined,
  currentUrl: string | null | undefined,
  conversationContext: Record<string, unknown> | null | undefined = undefined,
  opts: RewriteUrlOptions = {}
): string {
  if (!targetUrl || !config) return targetUrl;
  if (!HTTP_SCHEME_PREFIX.test(targetUrl)) return targetUrl;

  if (config.enabled === false) {
    if (opts.debug) {
      // eslint-disable-next-line no-console
      console.debug(`[dialonce-url-rewrite] config disabled, skipping. target=${targetUrl}`);
    }
    return targetUrl;
  }

  let url: URL;
  try {
    url = new URL(targetUrl);
  } catch {
    return targetUrl;
  }

  const parsedContext = parseContextUrl(currentUrl);
  const context = parsedContext ?? new URL(EMPTY_CONTEXT_URL);
  if (!HTTP_PROTOCOL.test(url.protocol)) return targetUrl;
  if (isLikelyStaticAssetUrl(url)) {
    if (opts.debug) {
      // eslint-disable-next-line no-console
      console.debug(`[dialonce-url-rewrite] static asset, skipping. target=${targetUrl}`);
    }
    return targetUrl;
  }

  const trace: string[] = [];

  // Tag each rule with its original index for debug traces.
  const indexed = config.rules.map((rule, originalIndex) => ({ rule, originalIndex }));

  if (opts.debug) {
    for (const { rule, originalIndex } of indexed) {
      if (rule.enabled === false) trace.push(`#${originalIndex} skipped (disabled)`);
    }
  }

  const activeRules = indexed.filter(({ rule }) => rule.enabled !== false);

  for (const { rule, originalIndex } of activeRules) {
    if (!matchesRule(url, rule.match, conversationContext)) continue;
    try {
      applyActions(url, rule.actions, context, parsedContext != null, conversationContext);
      if (opts.debug) trace.push(`#${originalIndex} ${describeRule(rule)}`);
    } catch (e) {
      if (opts.debug) trace.push(`#${originalIndex} FAILED ${(e as Error).message}`);
    }
    if (rule.stopOnMatch) break;
  }

  const result = url.toString();

  if (opts.debug) {
    // eslint-disable-next-line no-console
    console.debug(
      `[dialonce-url-rewrite] target=${targetUrl} host=${
        currentUrl ?? "(missing)"
      } -> ${result} rules=[${trace.join(", ")}]`
    );
  }

  return result;
}

export function matchesRule(
  url: URL,
  match: UrlRewriteMatchCriteria,
  conversationContext: Record<string, unknown> | null | undefined = undefined
): boolean {
  if (match.context) {
    for (const condition of match.context) {
      if (!matchesContextCondition(condition, conversationContext)) return false;
    }
  }

  if (match.allUrls) return true;

  const hostname = url.hostname.toLowerCase();

  if (match.host) {
    const expected = match.host.toLowerCase();
    if (expected.startsWith("*.")) {
      const suffix = expected.slice(1);
      if (!hostname.endsWith(suffix)) return false;
    } else if (hostname !== expected) {
      return false;
    }
  }

  if (match.pathPattern) {
    let regex: RegExp;
    try {
      regex = new RegExp(match.pathPattern);
    } catch {
      return false;
    }
    if (!regex.test(url.pathname)) return false;
  }

  if (match.queryContains) {
    for (const [k, v] of Object.entries(match.queryContains)) {
      if (url.searchParams.get(k) !== v) return false;
    }
  }

  const hasAnyCriterion =
    !!match.host ||
    !!match.pathPattern ||
    !!match.queryContains ||
    !!(match.context && match.context.length > 0);
  return hasAnyCriterion;
}

export function applyActions(
  url: URL,
  actions: UrlRewriteAction[],
  context: URL,
  hasValidContext = true,
  conversationContext: Record<string, unknown> | null | undefined = undefined
): void {
  for (const action of actions) {
    try {
      applyAction(url, action, context, hasValidContext, conversationContext);
    } catch {
      // Single action failure must not abort the remaining actions.
    }
  }
}

function applyAction(
  url: URL,
  action: UrlRewriteAction,
  context: URL,
  hasValidContext: boolean,
  conversationContext: Record<string, unknown> | null | undefined
): void {
  switch (action.type) {
    case "appendParam":
      if (!url.searchParams.has(action.key)) {
        url.searchParams.append(action.key, action.value);
      }
      return;
    case "overwriteParam":
      url.searchParams.set(action.key, action.value);
      return;
    case "removeParam":
      url.searchParams.delete(action.key);
      return;
    case "transferParams":
      if (!hasValidContext) return;
      for (const key of action.keys) {
        const value = context.searchParams.get(key);
        if (value != null) url.searchParams.set(key, value);
      }
      return;
    case "rewriteKey":
      if (!hasValidContext) return;
      {
        const value = context.searchParams.get(action.sourceKey);
        if (value != null) url.searchParams.set(action.targetKey, value);
      }
      return;
    case "insertContextValue": {
      const resolved = resolveContextValue(conversationContext, action.contextKey);
      const value =
        resolved != null ? String(resolved) : action.fallback != null ? action.fallback : null;
      if (value == null) return;
      if (action.target.type === "queryParam") {
        url.searchParams.set(action.target.key, value);
        return;
      }
      // pathPlaceholder — URL parsing percent-encodes chars like `{`/`}`, so
      // decode before substituting and let the URL API re-encode the result.
      {
        const placeholder = action.target.placeholder;
        let decodedPath: string;
        try {
          decodedPath = decodeURIComponent(url.pathname);
        } catch {
          decodedPath = url.pathname;
        }
        if (!decodedPath.includes(placeholder)) return;
        url.pathname = decodedPath.split(placeholder).join(value);
      }
      return;
    }
    case "insertContextBlob": {
      let blob: unknown;
      if (action.template) {
        blob = renderBlobTemplate(action.template, conversationContext);
      } else {
        const flatBlob: Record<string, unknown> = {};
        for (const key of action.contextKeys ?? []) {
          const resolved = resolveContextValue(conversationContext, key);
          if (resolved != null) flatBlob[key] = resolved;
        }
        blob = flatBlob;
      }
      // Always set the param (even for empty blob) so the action is observable.
      url.searchParams.set(action.targetParam, encodeBase64Utf8(JSON.stringify(blob)));
      return;
    }
    case "overwriteUrl": {
      const next = new URL(action.url);
      url.href = next.href;
      return;
    }
    case "appendPathSegment": {
      const resolved = resolveContextValue(conversationContext, action.contextKey);
      if (resolved == null) return;
      // One segment: a `/` inside the value is encoded, not treated as a separator.
      const base = url.pathname.endsWith("/") ? url.pathname : `${url.pathname}/`;
      url.pathname = `${base}${encodeURIComponent(String(resolved))}`;
      return;
    }
  }
}

function describeRule(rule: UrlRewriteRule): string {
  const matchParts: string[] = [];
  if (rule.match.allUrls) matchParts.push("allUrls");
  if (rule.match.host) matchParts.push(`host=${rule.match.host}`);
  if (rule.match.pathPattern) matchParts.push(`path=${rule.match.pathPattern}`);
  if (rule.match.queryContains)
    matchParts.push(`query=${Object.keys(rule.match.queryContains).join(",")}`);
  if (rule.match.context)
    matchParts.push(`context=${rule.match.context.map((c) => c.type).join("+")}`);
  const actionsDesc = rule.actions.map((a) => a.type).join("+");
  return `match(${matchParts.join("&") || "-"}) actions(${actionsDesc})`;
}
